import React, { Component } from 'react';
import ActionModal, { ActionModalFooter } from 'common-components/modal/ActionModal';
import { Text, FieldLabel } from 'common-components/typography';
import { PrimaryButton, SecondaryButton } from 'common-components/buttons';
import _ from 'lodash';
import { IShiftSlot } from 'interfaces/shift-interfaces';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { connect } from 'react-redux';
import { Spinner } from '@blueprintjs/core/lib/esm/components';
import { ISession } from 'interfaces/session-interfaces';
import DatePicker from 'react-datepicker';
import moment, { Moment } from 'moment-timezone';
import CommonUtils from 'utilities/common-utils';
import TimeInput from 'common-components/time-input/TimeInput';

interface IMarkAsAttendedModalProps {
  isOpen: boolean;
  shiftSlot: IShiftSlot;
  onClose: any;
  doMarkShiftAsAttended: any;
  session: ISession;
}

interface IMarkAsAttendedModalState {
  step: number;
  canManuallyClose: boolean;

  shiftStartDate: Moment;
  shiftEndDate: Moment;

  refreshShiftSlots: boolean;
}

class MarkAsAttendedModal extends Component<IMarkAsAttendedModalProps, IMarkAsAttendedModalState> {
  state = {
    step: 1,
    canManuallyClose: true,
    refreshShiftSlots: false,
    shiftStartDate: moment(),
    shiftEndDate: moment(),
  };

  private _reset = () =>
    this.setState({ step: 1, refreshShiftSlots: false, shiftStartDate: moment(), shiftEndDate: moment() });

  private _onSave = async (refreshShiftSlots = false) => {
    const { doMarkShiftAsAttended, session, shiftSlot } = this.props;
    const { shiftStartDate, shiftEndDate } = this.state;
    this.setState({ step: 2, canManuallyClose: false });

    const request = {
      serviceId: session.serviceId,
      serviceDateTimeId: session.serviceDateTimeId,
      supportWorkerAttendanceId: shiftSlot.supportWorkerAttendanceId,
      startDateTime: moment.tz(moment(shiftStartDate).format('YYYY-MM-DD HH:mm'), session.timezone),
      endDateTime: moment.tz(moment(shiftEndDate).format('YYYY-MM-DD HH:mm'), session.timezone),
    };

    await doMarkShiftAsAttended(request);

    this.setState({ step: 3, canManuallyClose: true, refreshShiftSlots });
  };

  private _onChangeStartDate = (date) => {
    this.setState({ shiftStartDate: moment(CommonUtils.formatCeilingDateTime(date)) });
    const minEndDate = moment(date).add(1, 'hour');

    if (this.state.shiftEndDate < minEndDate) {
      this.setState({ shiftEndDate: moment(minEndDate) });
    }
  };

  private _onChangeEndDate = (date) => {
    if (moment(date).isAfter(this.state.shiftStartDate)) {
      this.setState({ shiftEndDate: moment(CommonUtils.formatCeilingDateTime(date)) });
    } else {
      this.setState({ shiftEndDate: this.state.shiftEndDate });
    }
  };

  private _onClose = () => {
    const { onClose } = this.props;
    onClose({ targetFlag: 'isMarkAsAttendedOpen' }, this.state.refreshShiftSlots);
    this._reset();
  };

  componentDidUpdate(prevProps: Readonly<IMarkAsAttendedModalProps>) {
    if (this.props.isOpen !== prevProps.isOpen) {
      if (this.props.isOpen) {
        const { shiftSlot } = this.props;
        const { shiftStartDateTime, shiftEndDateTime } = shiftSlot;
        this.setState({
          shiftStartDate: moment(moment.tz(shiftStartDateTime, this.props.session.timezone).format('YYYY-MM-DD HH:mm')),
          shiftEndDate: moment(moment.tz(shiftEndDateTime, this.props.session.timezone).format('YYYY-MM-DD HH:mm')),
        });
      }
    }
  }

  render() {
    const { shiftSlot, session } = this.props;

    if (_.isEmpty(shiftSlot)) {
      return <></>;
    }

    const { supportWorkerId } = shiftSlot;

    if (_.isEmpty(supportWorkerId)) {
      return <></>;
    }

    const { shiftStartDateTime, shiftEndDateTime, shiftSlotStatus } = shiftSlot;

    const shiftDatesWithoutTimezone = {
      shiftStartDateTime: moment(moment.tz(shiftStartDateTime, session.timezone).format('YYYY-MM-DD HH:mm')),
      shiftEndDateTime: moment(moment.tz(shiftEndDateTime, session.timezone).format('YYYY-MM-DD HH:mm')),
    };

    return (
      <ActionModal
        isOpen={this.props.isOpen}
        title="Mark as attended"
        onClose={this._onClose}
        canCloseOutside={this.state.canManuallyClose}
      >
        {this.state.step === 1 && (
          <div>
            <div className="mb-medium line-height-135">
              <Text lineHeight={135}>
                You are indicating that{' '}
                <b>
                  {shiftSlot.firstName} {shiftSlot.lastName}
                </b>{' '}
                attended this session. Please indicate his start and end time below.
              </Text>
            </div>

            <div className="mb-large flex-row line-height-135">
              <div className="flex-1 mr-large">
                <FieldLabel text={'START TIME'} />

                <div className="flex-row">
                  {/*<DatePicker size="large" style={{ width: '144px' }} className="mr-medium" />*/}
                  <DatePicker
                    className="gh-datepicker rounded mr-medium"
                    calendarClassName="gh-datepicker-calendar"
                    onChange={this._onChangeStartDate}
                    dateFormat={'dd/MM/yyyy'}
                    isClearable={false}
                    selected={this.state.shiftStartDate.toDate()}
                    minDate={shiftDatesWithoutTimezone.shiftStartDateTime.toDate()}
                  />

                  <TimeInput
                    size="large"
                    value={moment(this.state.shiftStartDate)}
                    onChange={this._onChangeStartDate}
                  />
                </div>
              </div>
              <div className="flex-1">
                <FieldLabel text={'END TIME'} />

                <div className="flex-row">
                  <DatePicker
                    className="gh-datepicker rounded mr-medium"
                    calendarClassName="gh-datepicker-calendar"
                    onChange={this._onChangeEndDate}
                    dateFormat={'dd/MM/yyyy'}
                    isClearable={false}
                    selected={this.state.shiftEndDate.toDate()}
                    minDate={shiftDatesWithoutTimezone.shiftEndDateTime.toDate()}
                  />

                  <TimeInput size="large" value={moment(this.state.shiftEndDate)} onChange={this._onChangeEndDate} />
                </div>
              </div>
            </div>

            <ActionModalFooter align="right">
              <SecondaryButton size="large" className="mr-medium" onClick={this._onClose}>
                Cancel
              </SecondaryButton>
              <PrimaryButton size="large" onClick={() => this._onSave(true)}>
                Save
              </PrimaryButton>
            </ActionModalFooter>
          </div>
        )}

        {this.state.step === 2 && (
          <div className="line-height-135 anim-slide-left">
            <div className="flex-column align-center mv-large justify-center">
              <div className="mb-medium">
                <Spinner size={80} />
              </div>
              <div className="text-align-center">
                <Text color="secondary" weight="bold">
                  Marking shift as attended...
                </Text>
                <br />
                <Text color="secondary">This won't take long.</Text>
              </div>
            </div>
          </div>
        )}

        {this.state.step === 3 && (
          <div className="anim-fade-in-fast">
            <div className="mb-medium line-height-135">
              <Text lineHeight={135}>
                You have marked{' '}
                <b>
                  {shiftSlot.firstName} {shiftSlot.lastName}
                </b>{' '}
                as <b>Attended</b> for this shift.
              </Text>
            </div>

            <ActionModalFooter align="right">
              <PrimaryButton size="large" onClick={this._onClose}>
                Close
              </PrimaryButton>
            </ActionModalFooter>
          </div>
        )}
      </ActionModal>
    );
  }
}

const mapState = (state: IRootState) => ({});

const mapDispatch = (dispatch: IRootDispatch) => ({
  doMarkShiftAsAttended: dispatch.groupServiceStore.doMarkShiftAsAttended,
});

export default connect(mapState, mapDispatch)(MarkAsAttendedModal);
